import { useEffect, useId } from "react";
import { createPortal } from "react-dom";
import type { ReactNode } from "react";
import { useIntl } from "react-intl";
import { Button } from "./Button";

// App-wide modal primitive. Blur-material backdrop; title sits above the inner padding, footer
// holds actions. How it closes follows from what closing would lose — see `dismiss`.

/**
 * - `light`: nothing to lose (look, pick, export options). × in the title row, Esc, backdrop click.
 * - `explicit`: holds a draft or asks a question. Only the dialog's own buttons close it: the
 *   backdrop is inert, there is no ×, and Esc closes only while `dirty` is false — so it cancels a
 *   confirmation or an untouched form, but never throws away something the user typed.
 */
export type DialogDismiss = "light" | "explicit";

interface ModalProps {
  open: boolean;
  // Testanker; sitzt auf dem Dialogkasten.
  id?: string;
  onClose: () => void;
  /** Defaults to `light`. Any dialog that holds a draft or asks a question must say `explicit`. */
  dismiss?: DialogDismiss;
  /** `explicit` only: the draft differs from what was opened, so Esc no longer closes. */
  dirty?: boolean;
  /** An operation is in flight: nothing closes the dialog, and the × is disabled. */
  preventClose?: boolean;
  /** Testanker des ×; ohne Angabe `${id}-close`. */
  closeId?: string;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: string;
  /**
   * Accessible name for dialogs that render their own heading in `children` instead of passing
   * `title` (the agent sheet does). Ignored when `title` is set — that path labels itself.
   */
  ariaLabel?: string;
  // Spacious dialog for data-dense content (e.g. the LinkedIn import review): it
  // grows toward the viewport but deliberately KEEPS a backdrop margin + blur so
  // the platform stays visible behind it — the user knows they're still in Smycles,
  // not on a separate page. Body scrolls between a pinned title and footer.
  large?: boolean;
  fillViewport?: boolean;
}

export function Modal({
  id,
  open,
  onClose,
  dismiss = "light",
  dirty = false,
  preventClose = false,
  closeId = id ? `${id}-close` : undefined,
  title,
  children,
  footer,
  maxWidth = "32rem",
  large = false,
  fillViewport = false,
  ariaLabel,
}: ModalProps) {
  const intl = useIntl();
  const titleId = useId();
  const light = dismiss === "light";
  const escCloses = !preventClose && (light || !dirty);
  useEffect(() => {
    if (!open || !escCloses) return;
    const onKey = (e: KeyboardEvent) => {
      // A control inside the dialog that consumed Escape for itself (preventDefault, as the
      // DatePicker does) must not also close the dialog around it.
      if (e.key === "Escape" && !e.defaultPrevented) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, escCloses, onClose]);

  if (!open) return null;

  // Negative margins keep the 32px hit area from growing the micro-title row.
  const closeButton = (
    <Button
      id={closeId}
      variant="icon"
      size="sm"
      className="-my-2 -mr-3 shrink-0"
      aria-label={intl.formatMessage({ id: "common.close", defaultMessage: "Close" })}
      disabled={preventClose}
      onClick={onClose}
    >
      ✕
    </Button>
  );

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-ink/30 backdrop-blur-md"
        onClick={light && !preventClose ? onClose : undefined}
      />
      <div
        id={id}
        role="dialog"
        aria-modal="true"
        // role="dialog" with no name is announced as just "dialog". Prefer the visible title,
        // fall back to an explicit label for dialogs that head themselves inside `children`.
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : ariaLabel}
        className={
          large
            // Large but bounded: max-h-[90vh] keeps a top/bottom gap so the blurred
            // platform stays visible; body is the single flex-growing scroll region so
            // the footer stays pinned. Width cap is set via style (below).
            ? `relative flex max-h-[90vh] w-full flex-col overflow-hidden rounded-md bg-surface shadow-xl ring-1 ring-ink/10 ${fillViewport ? "h-[90vh]" : ""}`
            : "relative max-h-[85vh] w-full overflow-auto rounded-md bg-surface shadow-xl ring-1 ring-ink/10"
        }
        // Large: cap the width so the left/right gap to the viewport is HALF of the
        // former 72rem-centered margin — (100vw − W)/2 = ((100vw − 72rem)/2)/2 when
        // W = (100vw + 72rem)/2. Top/bottom keep the max-h-[90vh] gap. Windowed: fixed.
        style={large ? { maxWidth: "calc((100vw + 72rem) / 2)" } : { maxWidth }}
      >
        {title ? (
          <div className={`flex items-start justify-between gap-4 px-6 pb-3 pt-5 ${large ? "shrink-0" : ""}`}>
            <div id={titleId} className="min-w-0 text-smycles-micro tracking-[0.25em] text-ink-muted">
              {title}
            </div>
            {light ? closeButton : null}
          </div>
        ) : null}
        {/* Large: the body is the single flex-growing, scrolling region so the footer
            stays pinned; windowed: the whole box scrolls as before. */}
        {/* The portal has no main ancestor; shared content measures the dialog body instead. */}
        <div className={large ? "@container flex min-h-0 flex-1 flex-col overflow-hidden px-6 pb-6" : "@container px-6 pb-6"}>
          {children}
        </div>
        {footer ? (
          <div className={`flex justify-end gap-2 border-t border-ink/5 px-6 py-4 ${large ? "shrink-0" : ""}`}>
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
